import React, { FC, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { font } from '../lib/sharedStyles';
import { FormattedCategory } from './CategoryWrapper';
import { getCloudflareImageUrl } from '../lib/getCloudflareImageUrl';

interface CategoryModelTileProps extends FormattedCategory {
  slideBackground: string;
  slideHoverBackground: string;
  transparentProductImage: boolean;
  ctaText: string;
}

const Tile = styled.a<{
  $isHovered: boolean;
  $background: string;
  $hoverBackground: string;
  $transparentProductImage: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  text-decoration: none;
  transition: background 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
  background: ${({ $background }) => $background};
  min-width: 175px;

  &:hover {
    background: ${({ $hoverBackground }) => $hoverBackground};
  }

  @media (min-width: 768px) {
    min-width: 312px;
  }
`;

const ImageContainer = styled.div<{
  $isHovered: boolean;
  $transparentProductImage: boolean;
  $hoverImageUrl?: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  height: 299px;
  width: 98px;
  justify-content: flex-start;

  img:nth-of-type(1) {
    opacity: ${({ $isHovered, $hoverImageUrl }) =>
      $hoverImageUrl && $isHovered ? 0 : 1};
    transition: opacity 0.3s ease;
  }

  img:nth-of-type(2) {
    opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
    transition: opacity 0.3s ease;
  }

  @media (min-width: 768px) {
    height: 498px;
    width: 165px;
  }

  ${({ $transparentProductImage }) =>
    $transparentProductImage &&
    `
      overflow: visible;
      margin-top: 32px;
      justify-content: flex-end;
    `}
`;

const Image = styled.img<{ $transparentProductImage: boolean }>`
  position: absolute;
  object-fit: contain;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;

  ${({ $transparentProductImage }) =>
    $transparentProductImage &&
    `
      filter: drop-shadow(12px 12px 10px #ccc);
    `}
`;

const HoverImage = styled.img<{ $transparentProductImage: boolean }>`
  position: absolute;
  height: 100%;
  bottom: 0;
  left: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
  transform: translateX(-50%);

  ${({ $transparentProductImage }) =>
    $transparentProductImage &&
    `
      filter: drop-shadow(12px 12px 10px #ccc);
    `}
`;

const ContentContainer = styled.div<{ $isActive: boolean }>`
  opacity: ${({ $isActive }) => ($isActive ? '1' : '0')};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-bottom: 32px;

  @media (min-width: 768px) {
    opacity: 1;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  max-width: 230px;
`;

const Title = styled.span`
  ${font('Futura', 500, '16px', '16px')}
  letter-spacing: -0.32px;
  margin-top: 24px;

  @media (min-width: 768px) {
    ${font('Futura', 500, '24px', '24px')}
    letter-spacing: -0.48px;
    margin-top: 40px;
  }
`;

const ProductCount = styled.span`
  ${font('Instrument Sans', 400, '12px', '18px')}
  text-align: center;
  letter-spacing: 0.12px;
  margin-top: 4px;

  @media (min-width: 768px) {
    margin-top: 6px;
  }
`;

const Description = styled.span`
  ${font('Instrument Sans', 400, '12px', '18px')}
  text-align: center;
  letter-spacing: 0.12px;
  margin-top: 8px;
  transition: opacity 0.3s ease;
  padding: 0 5px;

  @media (min-width: 768px) {
    ${font('Instrument Sans', 400, '14px', '20px')}
    letter-spacing: 0.14px;
    margin-top: 10px;
  }
`;

const CTAWrapper = styled.div<{ $isHovered: boolean }>`
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  transition: opacity 0.3s ease;

  svg {
    fill: ${({ $isHovered }) => ($isHovered ? '#000' : '#fff')} !important;
    border-radius: 50%;
    background: ${({ $isHovered }) => ($isHovered ? '#fff' : '#000')};
  }

  @media (min-width: 768px) {
    opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  }
`;

const StyledCTAText = styled.span`
  ${font('Instrument Sans', 500, '16px', '20px')}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding: 10px 24px;
  border-radius: 64px;
  transition: all 0.5s cubic-bezier(0, 0, 0.23, 1);
  background-color: #1A1B1B;
  white-space: nowrap;

  &:hover {
    background-color: #333;
  }

  @media (min-width: 768px) {
    padding: 17px 50px;
  }
`;

const CategoryModelTile: FC<CategoryModelTileProps> = ({
  ctaText,
  url,
  title,
  productCount,
  description,
  imageUrl,
  hoverImageUrl,
  slideBackground,
  slideHoverBackground,
  transparentProductImage
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const tileRef = useRef<HTMLAnchorElement | null>(null);

  // Observe Swiper's active class on the parent slide
  useEffect(() => {
    const slideEl = tileRef.current?.parentElement;
    if (!slideEl) return;

    const update = () =>
      setIsActive(slideEl.classList.contains("swiper-slide-active"));

    update(); // initial
    const obs = new MutationObserver(update);
    obs.observe(slideEl, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return (
    <Tile
      ref={tileRef}
      href={url}
      data-category={title}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      $isHovered={isHovered}
      $background={slideBackground}
      $hoverBackground={slideHoverBackground}
      $transparentProductImage={transparentProductImage}
    >
      <ImageContainer
        $isHovered={isHovered}
        $transparentProductImage={transparentProductImage}
        $hoverImageUrl={hoverImageUrl}
      >
        {imageUrl && (
          <Image
            src={getCloudflareImageUrl(imageUrl)}
            alt={title}
            $transparentProductImage={transparentProductImage}
          />
        )}
        {hoverImageUrl && (
          <HoverImage
            src={getCloudflareImageUrl(hoverImageUrl)}
            alt={`${title} hover`}
            $transparentProductImage={transparentProductImage}
          />
        )}
      </ImageContainer>

      <ContentContainer $isActive={isActive}>
        <TextContainer>
          <Title>{title}</Title>
          {productCount && <ProductCount>{productCount} options</ProductCount>}
          {description && <Description>{description}</Description>}
        </TextContainer>
        {url && (
          <CTAWrapper $isHovered={isHovered}>
            {ctaText && <StyledCTAText>{ctaText}</StyledCTAText>}
          </CTAWrapper>
        )}
      </ContentContainer>
    </Tile>
  );
};

export default CategoryModelTile;
